import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Card, Metric, SectionLabel } from "@/components/common";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { BarChart3 } from "lucide-react";

export default function Analytics() {
  const { data } = usePolling(async () => (await api.get("/analytics")).data, 0, []);
  if (!data) return <div className="text-sm text-[var(--text-muted)]">Loading analytics…</div>;
  if (data.empty) {
    return (
      <Card className="p-10 text-center max-w-lg">
        <div className="text-sm font-semibold text-[var(--text-sec)]">No closed trades yet</div>
        <p className="mt-1 text-xs text-[var(--text-muted)]">Complete a few paper trades to unlock performance analytics.</p>
      </Card>
    );
  }
  const stats = [
    ["Total Trades", data.total_trades], ["Win Rate", `${data.win_rate}%`],
    ["Avg R", data.avg_r], ["Expectancy", `$${data.expectancy}`],
    ["Profit Factor", data.profit_factor], ["Max Drawdown", `$${data.max_drawdown}`],
    ["Avg Winner", `$${data.avg_win}`], ["Avg Loser", `$${data.avg_loss}`],
    ["Max Consec. Wins", data.max_consecutive_wins], ["Max Consec. Losses", data.max_consecutive_losses],
  ];
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center gap-3"><BarChart3 className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Performance Analytics</h1></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map(([l, v]) => (
          <Card key={l} className="p-4"><Metric label={l} value={v} /></Card>
        ))}
      </div>
      <Card className="p-5">
        <SectionLabel>Equity Curve</SectionLabel>
        <div className="mt-3" style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.equity_curve}>
              <XAxis dataKey="i" stroke="#667085" fontSize={11} />
              <YAxis stroke="#667085" fontSize={11} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ background: "#171E28", border: "1px solid #212B3B", borderRadius: 8, color: "#F5F7FA" }} />
              <Line type="monotone" dataKey="equity" stroke="#D6AE57" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-5">
        <Breakdown title="By Strategy" data={data.by_strategy} />
        <Breakdown title="By Session" data={data.by_session} />
        <Breakdown title="By Market Regime" data={data.by_regime} />
        <Breakdown title="By Direction" data={data.by_direction} />
      </div>
    </div>
  );
}

function Breakdown({ title, data }) {
  const rows = Object.entries(data || {});
  return (
    <Card className="p-5">
      <SectionLabel>{title}</SectionLabel>
      <div className="mt-3 space-y-2">
        {rows.length ? rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-sec)]">{k}</span>
            <span className="tnum">
              <span className="text-[var(--text-muted)]">{v.wins}/{v.trades} · </span>
              <span className={v.pnl >= 0 ? "text-bull" : "text-bear"}>${v.pnl}</span>
            </span>
          </div>
        )) : <div className="text-xs text-[var(--text-muted)]">No data.</div>}
      </div>
    </Card>
  );
}
